// src/pages/settings/Contacts.js
import React, { useState, useEffect, useRef } from 'react';
import {
    Paper,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Box,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment,
    Snackbar,
    Tooltip,
    Slide,
    Avatar
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Search,
    Refresh,
    Close as CloseIcon,
    Person,
    Email,
    Phone,
    Message as MessageIcon,
    People,
    CheckCircle,
    Cancel,
    Visibility,
    Reply as ReplyIcon,
    Archive as ArchiveIcon,
    MarkEmailRead as MarkReadIcon,
    AccessTime,
    Drafts,
    Inbox,
    ChatBubbleOutlineOutlined
} from '@mui/icons-material';
import { authService } from '../../apiServices/authService';
import { BASE_URL } from '../../apiServices/APIs';
import { recordsCount, recordsPerPage } from '../../utils/helpers';
import LoadingState from '../../components/LoadingState';
import ErrorState from '../../components/ErrorState';

// API Endpoints
const APIContactList = '/api/contact/list';
const APIContactShow = '/api/contact/show';
const APIContactReply = '/api/contact/reply';
const APIContactStatus = '/api/contact/status';
const APIContactDelete = '/api/contact/delete';
const APIContactStats = '/api/contact/stats';

// Transition component for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const Contacts = () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [contacts, setContacts] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(recordsPerPage);
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    
    // View message dialog
    const [viewDialog, setViewDialog] = useState(false);
    const [viewingContact, setViewingContact] = useState(null);
    
    // Reply dialog
    const [replyDialog, setReplyDialog] = useState(false);
    const [replyMessage, setReplyMessage] = useState('');
    const [replyErrors, setReplyErrors] = useState({});
    const [sendingReply, setSendingReply] = useState(false);
    
    // Delete confirmation
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    // Stats
    const [stats, setStats] = useState({
        total: 0,
        unread: 0,
        pending: 0,
        replied: 0,
        archived: 0
    });

    // Use ref to track if component is mounted
    const isMounted = useRef(true);

    useEffect(() => {
        isMounted.current = true;
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        fetchContacts();
        fetchStats();
    }, []);

    const showMessage = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const fetchContacts = async () => {
        setLoading(true);
        setError(null);
        try {
            const api = authService.getApi();
            const response = await api.get(APIContactList);
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                const contactsData = Array.isArray(responseData.data) ? responseData.data : [];
                setContacts(contactsData);
            } else {
                setError(responseData.message);
                showMessage(responseData.message || 'Failed to fetch messages', 'error');
            }
        } catch (error) {
            console.error('Error fetching contacts:', error);
            setError("Failed to load data");
            showMessage('Error fetching messages', 'error');
        } finally {
            if (isMounted.current) {
                setLoading(false);
            }
        }
    };

    const fetchStats = async () => {
        try {
            const api = authService.getApi();
            const response = await api.get(APIContactStats);
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                const statsData = responseData.data;
                setStats({
                    total: statsData.total_messages || 0,
                    unread: statsData.unread_messages || 0,
                    pending: statsData.pending_messages || 0,
                    replied: statsData.replied_messages || 0,
                    archived: statsData.archived_messages || 0
                });
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // View contact message
    const handleViewContact = async (contact) => {
        try {
            const api = authService.getApi();
            const response = await api.get(`${APIContactShow}/${contact.id}`);
            const responseData = response.data;

            if (responseData.status === 'success') {
                setViewingContact(responseData.data);
                setViewDialog(true);
                // Refresh list to update read status
                fetchContacts();
            } else {
                showMessage(responseData.message || 'Failed to fetch message', 'error');
            }
        } catch (error) {
            console.error('Error fetching contact:', error);
            showMessage('Error fetching message details', 'error');
        }
    };

    // Open reply dialog
    const handleOpenReply = (contact) => {
        setViewingContact(contact);
        setReplyMessage('');
        setReplyErrors({});
        setReplyDialog(true);
    };

    // Close reply dialog
    const handleCloseReply = () => {
        setReplyDialog(false);
        setViewingContact(null);
        setReplyMessage('');
        setReplyErrors({});
    };

    // Handle reply input change
    const handleReplyChange = (e) => {
        setReplyMessage(e.target.value);
        if (replyErrors.reply_message) {
            setReplyErrors({});
        }
    };

    // Validate reply form
    const validateReply = () => {
        const errors = {};
        
        if (!replyMessage.trim()) {
            errors.reply_message = 'Reply message is required';
        } else if (replyMessage.length < 10) {
            errors.reply_message = 'Reply must be at least 10 characters';
        }
        
        setReplyErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Send reply
    const handleSendReply = async () => {
        if (!validateReply()) return;

        setSendingReply(true);
        try {
            const api = authService.getApi();
            const user = authService.getCurrentUser();
            
            const response = await api.post(APIContactReply, {
                contact_id: viewingContact.id,
                reply_message: replyMessage,
                user_id: user?.id
            });
            
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                showMessage('Reply sent successfully');
                handleCloseReply();
                fetchContacts();
                fetchStats();
            } else {
                showMessage(responseData.message || 'Failed to send reply', 'error');
            }
        } catch (error) {
            console.error('Error sending reply:', error);
            showMessage('Error sending reply', 'error');
        } finally {
            if (isMounted.current) {
                setSendingReply(false);
            }
        }
    };

    // Update contact status
    const handleUpdateStatus = async (contact, newStatus) => {
        try {
            const api = authService.getApi();
            const response = await api.post(APIContactStatus, {
                contact_id: contact.id,
                status: newStatus
            });
            
            const responseData = response.data;

            if (responseData.status === 'success' && isMounted.current) {
                showMessage(`Message marked as ${newStatus}`);
                fetchContacts();
                fetchStats();
            } else {
                showMessage(responseData.message || 'Failed to update status', 'error');
            }
        } catch (error) {
            console.error('Error updating status:', error);
            showMessage('Error updating status', 'error');
        }
    };

    // Delete contact
    const handleDelete = (contact) => {
        setDeleteConfirm(contact);
    };

    const confirmDelete = async () => {
        if (deleteConfirm) {
            try {
                const api = authService.getApi();
                const response = await api.post(APIContactDelete, {
                    contact_id: deleteConfirm.id
                });
                
                const responseData = response.data;

                if (responseData.status === 'success' && isMounted.current) {
                    showMessage('Message deleted successfully');
                    fetchContacts();
                    fetchStats();
                    setDeleteConfirm(null);
                } else {
                    showMessage(responseData.message || 'Failed to delete message', 'error');
                }
            } catch (error) {
                console.error('Error deleting contact:', error);
                showMessage('Error deleting message', 'error');
            }
        }
    };

    const handleRefresh = () => {
        setSearchTerm('');
        setStatusFilter('');
        fetchContacts();
        fetchStats();
    };

    // Get status color
    const getStatusColor = (status) => {
        switch (status) {
            case 'pending':
                return 'warning';
            case 'read':
                return 'info';
            case 'replied':
                return 'success';
            case 'archived':
                return 'default';
            default:
                return 'default';
        }
    };

    // Get status icon
    const getStatusIcon = (status) => {
        switch (status) {
            case 'pending':
                return <Inbox fontSize="small" />;
            case 'read':
                return <Drafts fontSize="small" />;
            case 'replied':
                return <ChatBubbleOutlineOutlined fontSize="small" />;
            case 'archived':
                return <ArchiveIcon fontSize="small" />;
            default:
                return <Inbox fontSize="small" />;
        }
    };

    const filteredContacts = contacts.filter(contact => {
        const matchesSearch = searchTerm === '' ||
            contact.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.mobile?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            contact.message_preview?.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesStatus = !statusFilter || contact.status === statusFilter;
        
        return matchesSearch && matchesStatus;
    });

    const paginatedContacts = filteredContacts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    if (loading) {
        return (
            <LoadingState
                message="Loading Messages..."
                size="large"
                fullPage
            />
        );
    }

    if (error) {
        return (
            <ErrorState
                error={error}
                onRetry={fetchContacts}
                title="Unable to Load Messages"
                fullPage
                retryText="Retry Loading"
            />
        );
    }

    return (
        <Box>
            <Paper sx={{ p: 3, borderRadius: 3, mb: 3 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <MessageIcon sx={{ color: '#8B4513' }} />
                        Contact Messages ({recordsCount(contacts.length)} Total)
                    </Typography>
                    <Button
                        variant="outlined"
                        startIcon={<Refresh />}
                        onClick={handleRefresh}
                        sx={{ borderColor: '#8B4513', color: '#8B4513' }}
                    >
                        Refresh
                    </Button>
                </Box>

                {/* Stats Chips */}
                <Box sx={{ mb: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Chip 
                        icon={<MessageIcon />}
                        label={`Total: ${stats.total}`} 
                        color="primary" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<Inbox />}
                        label={`Pending: ${stats.pending}`} 
                        color="warning" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<Drafts />}
                        label={`Unread: ${stats.unread}`} 
                        color="info" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<ChatBubbleOutlineOutlined />}
                        label={`Replied: ${stats.replied}`} 
                        color="success" 
                        variant="outlined"
                    />
                    <Chip 
                        icon={<ArchiveIcon />}
                        label={`Archived: ${stats.archived}`} 
                        color="default" 
                        variant="outlined"
                    />
                </Box>

                {/* Search and Filter */}
                <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search by name, email, mobile or message..."
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ flex: 1, minWidth: 250 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search />
                                </InputAdornment>
                            ),
                            endAdornment: searchTerm && (
                                <InputAdornment position="end">
                                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                                        <CloseIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Status</InputLabel>
                        <Select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            label="Status"
                        >
                            <MenuItem value="">All Status</MenuItem>
                            <MenuItem value="pending">Pending</MenuItem>
                            <MenuItem value="read">Read</MenuItem>
                            <MenuItem value="replied">Replied</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                {/* Table */}
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell>#</TableCell>
                                <TableCell>Name</TableCell>
                                <TableCell>Email / Mobile</TableCell>
                                <TableCell>Message</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Date</TableCell>
                                <TableCell align="center">Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {paginatedContacts.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={7} align="center">
                                        <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                            No messages found
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                paginatedContacts.map((contact, index) => (
                                    <TableRow 
                                        key={contact.id} 
                                        hover
                                        sx={{
                                            backgroundColor: !contact.is_read ? '#fdf8f5' : 'inherit',
                                            fontWeight: !contact.is_read ? 'bold' : 'normal'
                                        }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={page * rowsPerPage + index + 1}
                                                color="primary"
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: !contact.is_read ? 'bold' : 'medium' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Avatar sx={{ width: 32, height: 32, bgcolor: '#8B4513', fontSize: 14 }}>
                                                    {contact.name?.charAt(0)?.toUpperCase()}
                                                </Avatar>
                                                {contact.name}
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Email fontSize="small" color="action" />
                                                    <Typography variant="body2">{contact.email}</Typography>
                                                </Box>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                    <Phone fontSize="small" color="action" />
                                                    <Typography variant="body2">{contact.mobile}</Typography>
                                                </Box>
                                            </Box>
                                        </TableCell>
                                        <TableCell>
                                            <Typography 
                                                variant="body2" 
                                                sx={{ 
                                                    maxWidth: 200, 
                                                    overflow: 'hidden', 
                                                    textOverflow: 'ellipsis', 
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                {contact.message_preview}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                icon={getStatusIcon(contact.status)}
                                                label={contact.status_text || contact.status}
                                                color={getStatusColor(contact.status)}
                                                size="small"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                <Typography variant="caption" color="text.secondary">
                                                    <AccessTime sx={{ fontSize: 12, mr: 0.5, verticalAlign: 'middle' }} />
                                                    {contact.created_at_human || contact.created_at}
                                                </Typography>
                                            </Box>
                                        </TableCell>
                                        <TableCell align="center">
                                            <Tooltip title="View Message">
                                                <IconButton 
                                                    onClick={() => handleViewContact(contact)} 
                                                    sx={{ color: '#8B4513' }}
                                                    size="small"
                                                >
                                                    <Visibility />
                                                </IconButton>
                                            </Tooltip>
                                            {contact.status !== 'replied' && (
                                                <Tooltip title="Reply">
                                                    <IconButton 
                                                        onClick={() => handleOpenReply(contact)} 
                                                        color="primary"
                                                        size="small"
                                                    >
                                                        <ReplyIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {contact.status === 'pending' && (
                                                <Tooltip title="Mark as Read">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(contact, 'read')} 
                                                        color="info"
                                                        size="small"
                                                    >
                                                        <MarkReadIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            {contact.status !== 'archived' && (
                                                <Tooltip title="Archive">
                                                    <IconButton 
                                                        onClick={() => handleUpdateStatus(contact, 'archived')} 
                                                        color="default"
                                                        size="small"
                                                    >
                                                        <ArchiveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                            <Tooltip title="Delete">
                                                <IconButton 
                                                    onClick={() => handleDelete(contact)} 
                                                    color="error"
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={filteredContacts.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value, 10));
                        setPage(0);
                    }}
                />
            </Paper>

            {/* View Message Dialog */}
            <Dialog
                open={viewDialog}
                onClose={() => setViewDialog(false)}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <MessageIcon sx={{ color: '#8B4513' }} />
                        <b>Message Details</b>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {viewingContact && (
                        <Box sx={{ mt: 2 }}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fafafa' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                                            <Avatar sx={{ bgcolor: '#8B4513' }}>
                                                {viewingContact.name?.charAt(0)?.toUpperCase()}
                                            </Avatar>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                                                    {viewingContact.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {viewingContact.email}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    {viewingContact.mobile}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                                            <Chip 
                                                label={viewingContact.status_text} 
                                                color={getStatusColor(viewingContact.status)} 
                                                size="small" 
                                            />
                                            <Chip 
                                                label={viewingContact.created_at_human} 
                                                variant="outlined" 
                                                size="small" 
                                            />
                                        </Box>
                                    </Paper>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography variant="subtitle2" gutterBottom>
                                        Message:
                                    </Typography>
                                    <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fdf8f5', minHeight: 100 }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                            {viewingContact.message}
                                        </Typography>
                                    </Paper>
                                </Grid>
                                {viewingContact.admin_reply && (
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" gutterBottom sx={{ color: '#4caf50' }}>
                                            Admin Reply:
                                        </Typography>
                                        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#f0f9f0', borderColor: '#c8e6c9' }}>
                                            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                                                {viewingContact.admin_reply}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                Replied by: {viewingContact.replied_by} • {viewingContact.replied_at}
                                            </Typography>
                                        </Paper>
                                    </Grid>
                                )}
                            </Grid>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setViewDialog(false)} variant="contained" color="error">
                        Close
                    </Button>
                    {viewingContact?.status !== 'replied' && (
                        <Button 
                            onClick={() => {
                                setViewDialog(false);
                                handleOpenReply(viewingContact);
                            }} 
                            variant="contained"
                            startIcon={<ReplyIcon />}
                            sx={{
                                background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                                '&:hover': {
                                    background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                                }
                            }}
                        >
                            Reply
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Reply Dialog */}
            <Dialog
                open={replyDialog}
                onClose={handleCloseReply}
                TransitionComponent={Transition}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <ReplyIcon sx={{ color: '#8B4513' }} />
                        <b>Reply to Message</b>
                    </Box>
                    {viewingContact && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            To: {viewingContact.name} ({viewingContact.email})
                        </Typography>
                    )}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {viewingContact && (
                            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fafafa', maxHeight: 150, overflow: 'auto' }}>
                                <Typography variant="caption" color="text.secondary">
                                    Original Message:
                                </Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                                    {viewingContact.message_preview || viewingContact.message}
                                </Typography>
                            </Paper>
                        )}
                        <TextField
                            fullWidth
                            label="Your Reply *"
                            name="reply_message"
                            value={replyMessage}
                            onChange={handleReplyChange}
                            required
                            multiline
                            rows={6}
                            error={!!replyErrors.reply_message}
                            helperText={replyErrors.reply_message}
                            placeholder="Type your reply here... (minimum 10 characters)"
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                }
                            }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button 
                        onClick={handleCloseReply} 
                        variant="contained" 
                        color="error"
                        disabled={sendingReply}
                    >
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleSendReply} 
                        variant="contained"
                        disabled={sendingReply}
                        startIcon={sendingReply ? null : <ReplyIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                            }
                        }}
                    >
                        {sendingReply ? 'Sending...' : 'Send Reply'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={!!deleteConfirm}
                onClose={() => setDeleteConfirm(null)}
                TransitionComponent={Transition}
                disableEscapeKeyDown
            >
                <DialogTitle sx={{ textAlign: 'center', color: '#8B4513' }}>Confirm Delete Message</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete the message from <strong>"{deleteConfirm?.name}"</strong>?
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Email: {deleteConfirm?.email}
                    </Typography>
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        <strong>Warning:</strong> This action cannot be undone.
                    </Alert>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)} variant="contained" color="error">Cancel</Button>
                    <Button 
                        onClick={confirmDelete} 
                        variant="contained" 
                        startIcon={<DeleteIcon />}
                        sx={{
                            background: 'linear-gradient(135deg, #8B4513 0%, #A0522D 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #6B3410 0%, #8B4513 100%)',
                            }
                        }}
                    >
                        Confirm Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert 
                    onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default Contacts;